import { useEffect, useState } from "react";
import { supabase } from "@/platform/lib/supabase";
import { useSession } from "./use-session";

export interface EinsteinProfile {
  id: string;
  full_name: string | null;
  display_name: string | null;
  target_course: string | null;
  exam_date: string | null;
  weekly_hours: number | null;
  onboarding_completed: boolean;
  consent_research: boolean;
  consent_terms_at: string | null;
  is_admin: boolean;
}

interface UseProfileResult {
  profile: EinsteinProfile | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useProfile(): UseProfileResult {
  const { session, loading: sessionLoading } = useSession();
  const [profile, setProfile] = useState<EinsteinProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const userId = session?.user.id;

  async function fetchProfile(uid: string): Promise<void> {
    const { data, error } = await supabase
      .from("einstein_profiles")
      .select(
        "id, full_name, display_name, target_course, exam_date, weekly_hours, onboarding_completed, consent_research, consent_terms_at, is_admin",
      )
      .eq("id", uid)
      .maybeSingle();
    if (error) {
      console.error("[useProfile]", error);
      setProfile(null);
    } else {
      setProfile((data as EinsteinProfile | null) ?? null);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (sessionLoading) return;
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void fetchProfile(userId);
  }, [userId, sessionLoading]);

  return {
    profile,
    loading: sessionLoading || loading,
    refetch: async () => {
      if (userId) await fetchProfile(userId);
    },
  };
}
